import type {
    ScreenerQuestionContextOption,
    ScreenerQuestionContextQuestion,
    SmartApplyScreenerQuestionType,
} from '../../../__generated__/graphql-base-types';

/**
 * Mock screener questions for the Apply For Me review flow (IAFE-2643).
 *
 * `QuestionsPage` queries `smartApplyContext` for real, so these fixtures only feed the local-dev
 * mock link and the MSW story/test handlers — they never answer a production request. Shaped as
 * `ScreenerQuestionContextQuestion`, the exact payload of
 * `smartApplyContext(input: { draftId }).screenerQuestionContext.screenerQuestions`, and following
 * the Smart Apply conventions: option values are ordinal strings, not labels.
 */

const option = (value: string, label: string): ScreenerQuestionContextOption => ({
    __typename: 'ScreenerQuestionContextOption',
    value,
    label,
});

const YES_NO = [option('0', 'Yes'), option('1', 'No')];

interface QuestionSeed {
    id: string;
    type: SmartApplyScreenerQuestionType;
    labelHtml: string;
    required?: boolean;
    prefilled?: boolean;
    textHtml?: string;
    options?: ScreenerQuestionContextOption[];
    hierarchicalOptions?: ScreenerQuestionContextQuestion['hierarchicalOptions'];
    groupedQuestionFields?: ScreenerQuestionContextQuestion[];
    groupLabel?: string;
    addButtonLabel?: string;
    format?: ScreenerQuestionContextQuestion['format'];
    min?: string | null;
    max?: string | null;
    limit?: number | null;
    fileFormats?: string[];
    minSelectionCount?: number | null;
    maxSelectionCount?: number | null;
    formField: ScreenerQuestionContextQuestion['formField'];
}

const singleValued = (textValue: string | null): ScreenerQuestionContextQuestion['formField'] => ({
    __typename: 'ScreenerQuestionContextSingleValuedFormField',
    textValue,
    error: '',
});

const multiValued = (values: string[]): ScreenerQuestionContextQuestion['formField'] => ({
    __typename: 'ScreenerQuestionContextMultiValuedFormField',
    values,
    error: '',
});

const hierarchical = (
    entries: [key: string, entryValue: string][],
): ScreenerQuestionContextQuestion['formField'] => ({
    __typename: 'ScreenerQuestionContextHierarchicalFormField',
    entries: entries.map(([key, entryValue]) => ({
        __typename: 'ScreenerQuestionContextHierarchicalFormFieldEntry',
        key,
        entryValue,
    })),
    error: '',
});

const fileField = (slotValues: string[]): ScreenerQuestionContextQuestion['formField'] => ({
    __typename: 'ScreenerQuestionContextFileFormField',
    fileSlots: slotValues.map((slotValue) => ({
        __typename: 'ScreenerQuestionContextFileFormFieldSlot',
        slotValue,
    })),
    errors: [],
});

const multiFieldMarker = (): ScreenerQuestionContextQuestion['formField'] => ({
    __typename: 'ScreenerQuestionContextMultiFieldFormField',
    _unused: null,
});

/** Fills in the long tail of schema fields the page never reads. */
const question = (seed: QuestionSeed, index: number): ScreenerQuestionContextQuestion => ({
    __typename: 'ScreenerQuestionContextQuestion',
    id: seed.id,
    viewId: `q_${index}`,
    name: `q_${seed.id}`,
    type: seed.type,
    labelHtml: seed.labelHtml,
    textHtml: seed.textHtml ?? '',
    required: seed.required ?? false,
    prefilled: seed.prefilled ?? false,
    canBePrefilled: true,
    options: seed.options ?? [],
    hierarchicalOptions: seed.hierarchicalOptions ?? [],
    condition: null,
    formField: seed.formField,
    format: seed.format ?? 'NONE',
    fontSize: 0,
    min: seed.min ?? null,
    max: seed.max ?? null,
    limit: seed.limit ?? null,
    minCharCount: null,
    inputDatePattern: 'MM/dd/yyyy',
    outputDatePattern: seed.type === 'DATE' ? 'MM/dd/yyyy' : '',
    localizedDatePattern: 'mm/dd/yyyy',
    thousandSeparator: ',',
    decimalSeparator: '.',
    fileFormats: seed.fileFormats ?? [],
    minSelectionCount: seed.minSelectionCount ?? null,
    maxSelectionCount: seed.maxSelectionCount ?? null,
    groupLabel: seed.groupLabel ?? null,
    addButtonLabel: seed.addButtonLabel ?? null,
    minEntryCount: seed.groupedQuestionFields ? 1 : null,
    maxEntryCount: seed.groupedQuestionFields ? 15 : null,
    groupedQuestionFields: seed.groupedQuestionFields ?? null,
    qualification: null,
    validationRuleKey: null,
    acesPredictedAnswers: [],
    suggestedPrefillAnswers: [],
});

const build = (seeds: QuestionSeed[]): ScreenerQuestionContextQuestion[] => seeds.map(question);

/** Fully prefilled — this draft skips the questions step. */
const ALL_PREFILLED = build([
    {
        id: 'work_authorization',
        type: 'SELECT',
        labelHtml: 'Are you authorized to work in the United States?',
        required: true,
        prefilled: true,
        options: YES_NO,
        formField: singleValued('0'),
    },
    {
        id: 'customer_service_years',
        type: 'SELECT',
        labelHtml: 'How many years of customer service experience do you have?',
        required: true,
        prefilled: true,
        options: [
            option('0', 'Less than 1 year'),
            option('1', '1-3 years'),
            option('2', '3-5 years'),
            option('3', '5-8 years'),
            option('4', '8+ years'),
        ],
        formField: singleValued('2'),
    },
    {
        id: 'desired_salary',
        type: 'TEXT',
        labelHtml: 'What is your desired annual salary?',
        prefilled: true,
        limit: 100,
        formField: singleValued('$75,000'),
    },
]);

/** The epic's target case: everything prefilled except one question. */
const ONE_UNANSWERED = build([
    {
        id: 'forklift',
        type: 'SELECT',
        labelHtml: 'Can you operate a forklift?',
        required: true,
        prefilled: true,
        options: YES_NO,
        formField: singleValued('0'),
    },
    {
        id: 'shifts',
        type: 'MULTISELECT',
        labelHtml: 'Which shifts can you work?',
        prefilled: true,
        options: [
            option('0', 'Morning'),
            option('1', 'Afternoon'),
            option('2', 'Evening'),
            option('3', 'Weekends'),
        ],
        minSelectionCount: 1,
        formField: multiValued(['0', '2']),
    },
    {
        id: 'start_date',
        type: 'DATE',
        labelHtml: 'When can you start?',
        required: true,
        prefilled: false,
        // Bounds arrive in `outputDatePattern`, not ISO — see ScreenerQuestionField's DATE branch.
        min: '01/01/2026',
        max: '12/31/2027',
        formField: singleValued(null),
    },
]);

/** Exercises every question type, including the ones the page renders specially. */
const ALL_TYPES = build([
    {
        id: 'background_check_notice',
        type: 'INFORMATION',
        // INFORMATION blocks are employer-authored and render `textHtml` only, so labelHtml is
        // deliberately empty rather than carrying a string the page would silently drop.
        labelHtml: '',
        textHtml:
            '<b>Notice from BrightConnect Solutions:</b> this position requires a valid driver&rsquo;s licence and a pre-employment background check.',
        formField: singleValued(null),
    },
    {
        id: 'esignature',
        type: 'TEXT',
        labelHtml:
            '<b>E-SIGNATURE</b><br/><div>Please enter your name to e-sign.</div><br/>Full Name',
        required: true,
        prefilled: true,
        limit: 100,
        formField: singleValued('Janett Smith'),
    },
    {
        id: 'why_interested',
        type: 'TEXTAREA',
        labelHtml: 'Why are you interested in this role?',
        prefilled: true,
        limit: 1500,
        formField: singleValued(
            'I have spent the last five years leading customer support teams and I am looking for a role where I can keep growing that craft.',
        ),
    },
    {
        id: 'years_experience',
        type: 'TEXT',
        labelHtml: 'How many years of experience do you have?',
        required: true,
        prefilled: true,
        format: 'INTEGER',
        min: '0',
        max: '50',
        formField: singleValued('8'),
    },
    {
        id: 'relocate',
        type: 'MULTISELECT',
        labelHtml: 'Which countries would you relocate to?',
        prefilled: false,
        options: [
            option('0', 'United States'),
            option('1', 'Canada'),
            option('2', 'Mexico'),
            option('3', 'Belize'),
            option('4', 'Other'),
        ],
        formField: multiValued([]),
    },
    {
        id: 'location',
        type: 'HIERARCHICAL',
        labelHtml: 'Where are you located?',
        required: true,
        prefilled: true,
        // AnswerPreview joins hierarchical entryValues verbatim rather than resolving them
        // through `options`, so this question's option values are the labels themselves.
        options: [option('California', 'California'), option('Texas', 'Texas')],
        hierarchicalOptions: [
            {
                __typename: 'ScreenerQuestionContextHierarchicalOptionSet',
                id: 'cali_cities',
                options: [
                    option('San Francisco', 'San Francisco'),
                    option('Los Angeles', 'Los Angeles'),
                ],
                condition: {
                    __typename: 'ScreenerQuestionContextCondition',
                    id: 'location',
                    values: ['California'],
                },
            },
            {
                __typename: 'ScreenerQuestionContextHierarchicalOptionSet',
                id: 'texas_cities',
                options: [option('San Antonio', 'San Antonio'), option('Austin', 'Austin')],
                condition: {
                    __typename: 'ScreenerQuestionContextCondition',
                    id: 'location',
                    values: ['Texas'],
                },
            },
        ],
        formField: hierarchical([
            ['location', 'Texas'],
            ['texas_cities', 'Austin'],
        ]),
    },
    {
        id: 'page_1',
        type: 'PAGEBREAK',
        labelHtml: '',
        formField: singleValued(null),
    },
    {
        id: 'certification',
        type: 'FILE',
        labelHtml: 'Upload your certification.',
        prefilled: true,
        fileFormats: ['pdf', 'doc', 'docx'],
        max: '1',
        formField: fileField(['forklift-certification.pdf']),
    },
    {
        id: 'availability',
        type: 'SELECT',
        labelHtml: 'What is your preferred working pattern?',
        required: true,
        prefilled: true,
        options: [
            option('0', 'Full-time'),
            option('1', 'Part-time'),
            option('2', 'Contract'),
            option('3', 'Temporary'),
            option('4', 'Internship'),
            option('5', 'Volunteer'),
            option('6', 'Apprenticeship'),
        ],
        formField: singleValued('0'),
    },
    {
        id: 'previous_employers',
        type: 'MULTIFIELD',
        labelHtml: 'Tell us about your previous employers.',
        groupLabel: 'Previous employer',
        addButtonLabel: 'Add another employer',
        groupedQuestionFields: build([
            {
                id: 'employer_name',
                type: 'TEXT',
                labelHtml: 'Employer name',
                required: true,
                formField: singleValued(null),
            },
            {
                id: 'employer_role',
                type: 'TEXT',
                labelHtml: 'Job title',
                formField: singleValued(null),
            },
        ]),
        formField: multiFieldMarker(),
    },
]);

/**
 * A single unanswered *required* radio-card SELECT, and nothing else.
 *
 * SELECT at or below the dropdown cutoff renders through `QuestionField` + `RadioCard`, which
 * `ScreenerQuestionField` has to register with jspl by hand. Keeping it alone here means nothing
 * else can register and satisfy the validity gate on its behalf.
 */
const LONE_REQUIRED_RADIO_CARD = build([
    {
        id: 'cpr_certification',
        type: 'SELECT',
        labelHtml: 'Do you have a valid CPR certification?',
        required: true,
        prefilled: false,
        options: YES_NO,
        formField: singleValued(null),
    },
]);

/**
 * A required question the server calls prefilled but sent no answer for.
 *
 * The two come from separate fields, so they can disagree. Left alone this is the worst case for
 * the page: collapsed, it would mount no field, block the CTAs from page state and have nothing on
 * screen to explain itself — so it must render open from the first paint.
 */
const PREFILLED_BUT_EMPTY = build([
    {
        id: 'notice_period',
        type: 'TEXT',
        labelHtml: 'How much notice do you need to give?',
        required: true,
        prefilled: true,
        limit: 100,
        formField: singleValued(null),
    },
]);

/**
 * A single optional FILE question with no prefill, so the jspl upload control is available (unlike
 * the `max: 1` + prefilled certification in ALL_TYPES). Used by the FILE-upload feature story.
 */
const LONE_FILE = build([
    {
        id: 'certification',
        type: 'FILE',
        labelHtml: 'Upload your certification.',
        prefilled: false,
        fileFormats: ['pdf', 'doc', 'docx'],
        formField: fileField([]),
    },
]);

/**
 * Fixtures keyed to the ids the shared auto-apply mocks use. `draft-101` is the one the mocked
 * `GetAutoApplyDraftList` reports an outstanding question for, so it is the id that actually
 * reaches this page in local dev; the others are here for Storybook.
 */
export const dummyScreenerQuestionsByDraftId: Partial<
    Record<string, ScreenerQuestionContextQuestion[]>
> = {
    'draft-001': ALL_PREFILLED,
    'draft-002': ALL_PREFILLED,
    'draft-003': ALL_PREFILLED,
    'draft-004': ALL_PREFILLED,
    'draft-005': ALL_PREFILLED,
    'draft-101': ONE_UNANSWERED,
    'draft-102': ALL_TYPES,
    'draft-103': LONE_REQUIRED_RADIO_CARD,
    'draft-104': PREFILLED_BUT_EMPTY,
    'draft-105': LONE_FILE,
};

/**
 * Unkeyed ids fall back to the one-unanswered fixture, so a mocked draft that the
 * `unfilledScreenerQuestionCount` gate routed here still has something to ask.
 */
export const getScreenerQuestions = (draftId?: string): ScreenerQuestionContextQuestion[] =>
    (draftId && dummyScreenerQuestionsByDraftId[draftId]) || ONE_UNANSWERED;
